// ========== TYPES ==========

type FilingStatus = 'single' | 'mfj' | 'hoh'
type DebtStrategy = 'avalanche' | 'snowball'

interface Debt {
  balance: number
  rate: number
  minPayment: number
  name?: string
  type?: string
  [key: string]: unknown
}

interface LumpSum {
  amount: number
  month: number
  targetDebtIndex: number
}

interface MortgageExtrasInput {
  propertyTaxRate?: number
  pmiRate?: number
  annualInsurance?: number
  monthlyHOA?: number
}

interface MortgageExtras {
  monthlyPropertyTax: number
  monthlyInsurance: number
  monthlyPMI: number
  monthlyHOA: number
  totalMonthlyExtras: number
  pmiRate: number
}

interface RothVsTraditionalInput {
  grossIncome: number
  filingStatus?: string
  currentAge?: number
  retirementAge?: number
  annualContribution?: number
  expectedReturn?: number
  expectedRetirementIncome?: number
  stateTaxRate?: number
  employerMatch?: number
  matchAppliesTo?: 'both' | 'traditional' | 'roth'
  startingRothBalance?: number
  startingTradBalance?: number
}

// ========== LOAN CALCULATIONS ==========

export function calculateMonthlyPayment(principal: number, annualRate: number, termMonths: number): number {
  if (annualRate === 0) return principal / termMonths
  const r = annualRate / 100 / 12
  return principal * (r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1)
}

export function generateAmortization(principal: number, annualRate: number, termMonths: number, extraPayment = 0) {
  const r = annualRate / 100 / 12
  const basePayment = calculateMonthlyPayment(principal, annualRate, termMonths)
  const schedule = []
  let balance = principal
  let totalInterest = 0
  let totalPrincipal = 0

  for (let month = 1; month <= termMonths && balance > 0.01; month++) {
    const interest = balance * r
    const principalPaid = Math.min(basePayment - interest + extraPayment, balance)
    balance = Math.max(0, balance - principalPaid)
    totalInterest += interest
    totalPrincipal += principalPaid

    schedule.push({
      month,
      payment: Math.round((interest + principalPaid) * 100) / 100,
      principal: Math.round(principalPaid * 100) / 100,
      interest: Math.round(interest * 100) / 100,
      balance: Math.round(balance * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100,
      totalPrincipal: Math.round(totalPrincipal * 100) / 100,
    })

    if (balance <= 0) break
  }

  return schedule
}

export function calculateLoanSummary(principal: number, annualRate: number, termMonths: number, extraPayment = 0, downPayment = 0) {
  const loanAmount = principal - downPayment
  if (loanAmount <= 0) {
    return { monthlyPayment: 0, totalInterest: 0, totalCost: 0, actualTerm: 0, loanAmount: 0, schedule: [] }
  }
  const monthlyPayment = calculateMonthlyPayment(loanAmount, annualRate, termMonths)
  const schedule = generateAmortization(loanAmount, annualRate, termMonths, extraPayment)
  const totalInterest = schedule.reduce((s, r) => s + r.interest, 0)
  const totalPrincipal = schedule.reduce((s, r) => s + r.principal, 0)

  return {
    loanAmount: Math.round(loanAmount * 100) / 100,
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    totalPayment: Math.round((monthlyPayment + extraPayment) * 100) / 100,
    totalInterest: Math.round(totalInterest * 100) / 100,
    totalCost: Math.round((totalInterest + loanAmount + downPayment) * 100) / 100,
    actualTerm: schedule.length,
    interestSaved: 0,
    monthsSaved: 0,
    schedule,
  }
}

export function calculateInterestSavings(principal: number, annualRate: number, termMonths: number, extraPayment: number, downPayment = 0) {
  const base = calculateLoanSummary(principal, annualRate, termMonths, 0, downPayment)
  const withExtra = calculateLoanSummary(principal, annualRate, termMonths, extraPayment, downPayment)
  return {
    ...withExtra,
    interestSaved: Math.round((base.totalInterest - withExtra.totalInterest) * 100) / 100,
    monthsSaved: base.actualTerm - withExtra.actualTerm,
    baseInterest: base.totalInterest,
    baseTerm: base.actualTerm,
  }
}

// ========== INVESTMENT CALCULATIONS ==========

export function calculateInvestmentGrowth(initialAmount: number, monthlyContribution: number, annualReturn: number, years: number) {
  const r = annualReturn / 100 / 12
  const months = years * 12
  const schedule = []
  let balance = initialAmount
  let totalContributions = initialAmount

  for (let month = 1; month <= months; month++) {
    const growth = balance * r
    balance += growth + monthlyContribution
    totalContributions += monthlyContribution

    if (month % 12 === 0 || month === months) {
      schedule.push({
        year: Math.ceil(month / 12),
        month,
        balance: Math.round(balance * 100) / 100,
        contributions: Math.round(totalContributions * 100) / 100,
        growth: Math.round((balance - totalContributions) * 100) / 100,
      })
    }
  }

  return {
    finalBalance: Math.round(balance * 100) / 100,
    totalContributions: Math.round(totalContributions * 100) / 100,
    totalGrowth: Math.round((balance - totalContributions) * 100) / 100,
    schedule,
  }
}

export function compareInvestmentRates(initialAmount: number, monthlyContribution: number, years: number, rates: number[]) {
  return rates.map(rate => ({
    rate,
    ...calculateInvestmentGrowth(initialAmount, monthlyContribution, rate, years),
  }))
}

// ========== DEBT PAYOFF ==========

export const DEBT_PRESETS = [
  { type: 'credit_card', label: 'Credit Card', icon: '💳', defaultRate: 24.99, defaultMin: 25 },
  { type: 'student', label: 'Student Loan', icon: '🎓', defaultRate: 5.5, defaultMin: 200 },
  { type: 'auto', label: 'Auto Loan', icon: '🚗', defaultRate: 5.9, defaultMin: 350 },
  { type: 'personal', label: 'Personal Loan', icon: '💰', defaultRate: 10.5, defaultMin: 150 },
  { type: 'medical', label: 'Medical Debt', icon: '🏥', defaultRate: 0, defaultMin: 100 },
  { type: 'other', label: 'Other', icon: '📋', defaultRate: 6, defaultMin: 50 },
] as const

/**
 * Calculate debt payoff with optional lump sum payments.
 * @param {Array} debts - Array of debt objects
 * @param {string} strategy - 'avalanche' or 'snowball'
 * @param {number} extraMonthly - Extra monthly payment
 * @param {Object} lumpSum - { amount, month, targetDebtIndex } or null
 *   amount: one-time payment amount
 *   month: which month to apply it (1-based)
 *   targetDebtIndex: original debt index to target, or -1 for strategy-based targeting
 */
export function calculateDebtPayoff(debts: Debt[], strategy: DebtStrategy = 'avalanche', extraMonthly = 0, lumpSum: LumpSum | null = null) {
  if (!debts.length) return { schedule: [], totalInterest: 0, totalPaid: 0, months: 0, perDebt: [], lumpSumApplied: null }

  // Sort debts based on strategy
  const sorted = debts.map((d, i) => ({ ...d, originalIndex: i }))
  if (strategy === 'avalanche') {
    sorted.sort((a, b) => b.rate - a.rate) // highest rate first
  } else {
    sorted.sort((a, b) => a.balance - b.balance) // smallest balance first
  }

  const balances = sorted.map(d => d.balance)
  const rates = sorted.map(d => d.rate / 100 / 12)
  // Ensure minimum payment covers at least the first month's interest + $1 principal
  // to avoid infinite/never-payoff scenarios
  const mins = sorted.map((d, i) => {
    const monthlyInterest = balances[i] * rates[i]
    return Math.max(d.minPayment, Math.ceil(monthlyInterest) + 1)
  })
  const perDebtInterest = sorted.map(() => 0)
  const perDebtPaid = sorted.map(() => 0)
  const perDebtMonths = sorted.map(() => 0)
  let hitMaxMonths = false

  let lumpSumApplied = null

  const schedule = []
  let month = 0
  const MAX_MONTHS = 600 // 50-year cap

  while (balances.some(b => b > 0.01) && month < MAX_MONTHS) {
    month++
    let availableExtra = extraMonthly
    const monthData: { month: number; balances: number[]; totalBalance: number; payments: number[]; lumpApplied: boolean } = { month, balances: [], totalBalance: 0, payments: [], lumpApplied: false }

    // Apply minimum payments and interest
    for (let i = 0; i < balances.length; i++) {
      if (balances[i] <= 0.01) {
        monthData.payments.push(0)
        continue
      }
      const interest = balances[i] * rates[i]
      perDebtInterest[i] += interest
      const minPay = Math.min(mins[i], balances[i] + interest)
      balances[i] = balances[i] + interest - minPay
      perDebtPaid[i] += minPay
      monthData.payments.push(minPay)

      if (balances[i] <= 0.01) {
        balances[i] = 0
        if (perDebtMonths[i] === 0) perDebtMonths[i] = month
      }
    }

    // Apply lump sum payment in the specified month
    if (lumpSum && lumpSum.amount > 0 && month === lumpSum.month) {
      let lumpRemaining = lumpSum.amount

      // If targeting a specific debt
      if (lumpSum.targetDebtIndex >= 0) {
        const sortedIdx = sorted.findIndex(d => d.originalIndex === lumpSum.targetDebtIndex)
        if (sortedIdx >= 0 && balances[sortedIdx] > 0.01) {
          const applied = Math.min(lumpRemaining, balances[sortedIdx])
          balances[sortedIdx] -= applied
          perDebtPaid[sortedIdx] += applied
          monthData.payments[sortedIdx] += applied
          lumpRemaining -= applied
          if (balances[sortedIdx] <= 0.01) {
            balances[sortedIdx] = 0
            if (perDebtMonths[sortedIdx] === 0) perDebtMonths[sortedIdx] = month
            lumpRemaining += mins[sortedIdx] // freed-up minimum
          }
        }
      }

      // Apply any remaining lump sum using strategy order
      for (let i = 0; i < balances.length && lumpRemaining > 0; i++) {
        if (balances[i] <= 0.01) continue
        const applied = Math.min(lumpRemaining, balances[i])
        balances[i] -= applied
        perDebtPaid[i] += applied
        monthData.payments[i] += applied
        lumpRemaining -= applied
        if (balances[i] <= 0.01) {
          balances[i] = 0
          if (perDebtMonths[i] === 0) perDebtMonths[i] = month
          lumpRemaining += mins[i]
        }
      }

      monthData.lumpApplied = true
      lumpSumApplied = { month, amount: lumpSum.amount - lumpRemaining }
    }

    // Apply extra monthly payment to target debt (first non-zero in sorted order)
    for (let i = 0; i < balances.length && availableExtra > 0; i++) {
      if (balances[i] <= 0.01) continue
      const extraApplied = Math.min(availableExtra, balances[i])
      balances[i] -= extraApplied
      perDebtPaid[i] += extraApplied
      monthData.payments[i] += extraApplied
      availableExtra -= extraApplied

      if (balances[i] <= 0.01) {
        balances[i] = 0
        if (perDebtMonths[i] === 0) perDebtMonths[i] = month
        // Freed-up minimum flows to next target (debt snowball effect)
        availableExtra += mins[i]
      }
    }

    monthData.balances = [...balances]
    monthData.totalBalance = balances.reduce((s, b) => s + b, 0)
    schedule.push(monthData)
  }

  if (month >= MAX_MONTHS && balances.some(b => b > 0.01)) {
    hitMaxMonths = true
  }

  // Mark remaining debts
  for (let i = 0; i < balances.length; i++) {
    if (perDebtMonths[i] === 0 && balances[i] <= 0.01) perDebtMonths[i] = month
  }

  const totalInterest = perDebtInterest.reduce((s, v) => s + v, 0)
  const totalPaid = perDebtPaid.reduce((s, v) => s + v, 0)

  const perDebt = sorted.map((d, i) => ({
    ...d,
    totalInterest: Math.round(perDebtInterest[i] * 100) / 100,
    totalPaid: Math.round(perDebtPaid[i] * 100) / 100,
    monthsToPayoff: perDebtMonths[i],
  }))

  return {
    schedule,
    totalInterest: Math.round(totalInterest * 100) / 100,
    totalPaid: Math.round(totalPaid * 100) / 100,
    months: month,
    perDebt,
    lumpSumApplied,
    hitMaxMonths,
  }
}

/**
 * Calculate the extra monthly payment required to pay off all debts within a target number of months.
 * Uses binary search over calculateDebtPayoff to find the minimum extra payment.
 * @param {Array} debts - Array of debt objects
 * @param {string} strategy - 'avalanche' or 'snowball'
 * @param {number} targetMonths - Desired payoff timeframe in months
 * @param {Object} lumpSum - Optional lump sum (same format as calculateDebtPayoff)
 * @returns {{ requiredExtra, totalPayment, totalInterest, totalPaid, months, feasible, result }}
 */
export function calculateRequiredPayment(debts: Debt[], strategy: DebtStrategy = 'avalanche', targetMonths = 12, lumpSum: LumpSum | null = null) {
  if (!debts.length || targetMonths <= 0) {
    return { requiredExtra: 0, totalPayment: 0, totalInterest: 0, totalPaid: 0, months: 0, feasible: false, result: null }
  }

  const totalBalance = debts.reduce((s, d) => s + d.balance, 0)
  const totalMinPayments = debts.reduce((s, d) => s + d.minPayment, 0)

  // Check if minimum payments alone are enough
  const minResult = calculateDebtPayoff(debts, strategy, 0, lumpSum)
  if (minResult.months <= targetMonths) {
    return {
      requiredExtra: 0,
      totalPayment: totalMinPayments,
      totalInterest: minResult.totalInterest,
      totalPaid: minResult.totalPaid,
      months: minResult.months,
      feasible: true,
      result: minResult,
    }
  }

  // Binary search for the required extra payment
  let lo = 0
  let hi = totalBalance // Upper bound: pay off everything in ~1 month
  let bestExtra = hi
  let bestResult = null

  for (let iter = 0; iter < 50; iter++) {
    const mid = Math.round((lo + hi) / 2 * 100) / 100
    const r = calculateDebtPayoff(debts, strategy, mid, lumpSum)
    if (r.months <= targetMonths) {
      bestExtra = mid
      bestResult = r
      hi = mid
    } else {
      lo = mid
    }
    if (hi - lo < 0.5) break
  }

  // Round up to nearest dollar to ensure we actually meet the target
  bestExtra = Math.ceil(bestExtra)
  bestResult = calculateDebtPayoff(debts, strategy, bestExtra, lumpSum)

  return {
    requiredExtra: bestExtra,
    totalPayment: totalMinPayments + bestExtra,
    totalInterest: bestResult.totalInterest,
    totalPaid: bestResult.totalPaid,
    months: bestResult.months,
    feasible: bestResult.months <= targetMonths,
    result: bestResult,
  }
}

export function compareDebtStrategies(debts: Debt[], extraMonthly = 0, lumpSum: LumpSum | null = null) {
  const avalanche = calculateDebtPayoff(debts, 'avalanche', extraMonthly, lumpSum)
  const snowball = calculateDebtPayoff(debts, 'snowball', extraMonthly, lumpSum)
  return {
    avalanche,
    snowball,
    interestDifference: Math.round((snowball.totalInterest - avalanche.totalInterest) * 100) / 100,
    monthsDifference: snowball.months - avalanche.months,
  }
}

// ========== MORTGAGE EXTRAS ==========

export function calculateMortgageExtras(homePrice: number, loanAmount: number, { propertyTaxRate = 0, pmiRate = 0, annualInsurance = 0, monthlyHOA = 0 }: MortgageExtrasInput) {
  const monthlyPropertyTax = (homePrice * propertyTaxRate / 100) / 12
  const monthlyInsurance = annualInsurance / 12
  const monthlyPMI = (loanAmount * pmiRate / 100) / 12
  return {
    monthlyPropertyTax: Math.round(monthlyPropertyTax * 100) / 100,
    monthlyInsurance: Math.round(monthlyInsurance * 100) / 100,
    monthlyPMI: Math.round(monthlyPMI * 100) / 100,
    monthlyHOA: Math.round(monthlyHOA * 100) / 100,
    totalMonthlyExtras: Math.round((monthlyPropertyTax + monthlyInsurance + monthlyPMI + monthlyHOA) * 100) / 100,
  }
}

export function generateAmortizationWithPMI(principal: number, annualRate: number, termMonths: number, homePrice: number, extras: MortgageExtras, extraPayment = 0) {
  const r = annualRate / 100 / 12
  const basePayment = calculateMonthlyPayment(principal, annualRate, termMonths)
  const schedule = []
  let balance = principal
  let totalInterest = 0
  let totalPrincipal = 0
  let totalPMI = 0
  const pmiThreshold = homePrice * 0.80

  for (let month = 1; month <= termMonths && balance > 0.01; month++) {
    const interest = balance * r
    const principalPaid = Math.min(basePayment - interest + extraPayment, balance)
    balance = Math.max(0, balance - principalPaid)
    totalInterest += interest
    totalPrincipal += principalPaid

    const hasPMI = extras.pmiRate > 0 && (balance > pmiThreshold)
    const monthlyPMI = hasPMI ? (principal * extras.pmiRate / 100) / 12 : 0
    totalPMI += monthlyPMI

    const monthExtras = extras.monthlyPropertyTax + extras.monthlyInsurance + monthlyPMI + extras.monthlyHOA
    schedule.push({
      month,
      payment: Math.round((interest + principalPaid) * 100) / 100,
      principal: Math.round(principalPaid * 100) / 100,
      interest: Math.round(interest * 100) / 100,
      balance: Math.round(balance * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100,
      totalPrincipal: Math.round(totalPrincipal * 100) / 100,
      pmi: Math.round(monthlyPMI * 100) / 100,
      extras: Math.round(monthExtras * 100) / 100,
      totalWithExtras: Math.round((interest + principalPaid + monthExtras) * 100) / 100,
    })

    if (balance <= 0) break
  }

  return { schedule, totalPMI: Math.round(totalPMI * 100) / 100 }
}

// ========== LOAN TYPE PRESETS ==========

export const LOAN_PRESETS = {
  mortgage: { label: 'Mortgage', defaultRate: 6.5, defaultTerm: 360, hasDownPayment: true, isMortgage: true, icon: '🏠' },
  auto: { label: 'Auto Loan', defaultRate: 5.9, defaultTerm: 60, hasDownPayment: true, isMortgage: false, icon: '🚗' },
  credit_card: { label: 'Credit Card', defaultRate: 24.99, defaultTerm: 60, hasDownPayment: false, isMortgage: false, icon: '💳' },
  personal: { label: 'Personal Loan', defaultRate: 10.5, defaultTerm: 48, hasDownPayment: false, isMortgage: false, icon: '💰' },
  student: { label: 'Student Loan', defaultRate: 5.5, defaultTerm: 120, hasDownPayment: false, isMortgage: false, icon: '🎓' },
} as const

// ========== ROTH VS TRADITIONAL / TAX BRACKET CALCULATIONS ==========

// 2025 State Income Tax Rates (top marginal rate for progressive states, flat rate for flat-tax states)
export const STATE_TAX_DATA = [
  { code: 'NONE', name: 'No State Tax', rate: 0 },
  { code: 'AL', name: 'Alabama', rate: 5.0 },
  { code: 'AK', name: 'Alaska', rate: 0 },
  { code: 'AZ', name: 'Arizona', rate: 2.5 },
  { code: 'AR', name: 'Arkansas', rate: 3.9 },
  { code: 'CA', name: 'California', rate: 13.3 },
  { code: 'CO', name: 'Colorado', rate: 4.4 },
  { code: 'CT', name: 'Connecticut', rate: 6.99 },
  { code: 'DE', name: 'Delaware', rate: 6.6 },
  { code: 'DC', name: 'Washington DC', rate: 10.75 },
  { code: 'FL', name: 'Florida', rate: 0 },
  { code: 'GA', name: 'Georgia', rate: 5.39 },
  { code: 'HI', name: 'Hawaii', rate: 11.0 },
  { code: 'ID', name: 'Idaho', rate: 5.8 },
  { code: 'IL', name: 'Illinois', rate: 4.95 },
  { code: 'IN', name: 'Indiana', rate: 3.05 },
  { code: 'IA', name: 'Iowa', rate: 5.7 },
  { code: 'KS', name: 'Kansas', rate: 5.7 },
  { code: 'KY', name: 'Kentucky', rate: 4.0 },
  { code: 'LA', name: 'Louisiana', rate: 4.25 },
  { code: 'ME', name: 'Maine', rate: 7.15 },
  { code: 'MD', name: 'Maryland', rate: 5.75 },
  { code: 'MA', name: 'Massachusetts', rate: 5.0 },
  { code: 'MI', name: 'Michigan', rate: 4.25 },
  { code: 'MN', name: 'Minnesota', rate: 9.85 },
  { code: 'MS', name: 'Mississippi', rate: 5.0 },
  { code: 'MO', name: 'Missouri', rate: 4.8 },
  { code: 'MT', name: 'Montana', rate: 5.9 },
  { code: 'NE', name: 'Nebraska', rate: 5.84 },
  { code: 'NV', name: 'Nevada', rate: 0 },
  { code: 'NH', name: 'New Hampshire', rate: 0 },
  { code: 'NJ', name: 'New Jersey', rate: 10.75 },
  { code: 'NM', name: 'New Mexico', rate: 5.9 },
  { code: 'NY', name: 'New York', rate: 10.9 },
  { code: 'NC', name: 'North Carolina', rate: 4.5 },
  { code: 'ND', name: 'North Dakota', rate: 2.5 },
  { code: 'OH', name: 'Ohio', rate: 3.125 },
  { code: 'OK', name: 'Oklahoma', rate: 4.75 },
  { code: 'OR', name: 'Oregon', rate: 9.9 },
  { code: 'PA', name: 'Pennsylvania', rate: 3.07 },
  { code: 'RI', name: 'Rhode Island', rate: 5.99 },
  { code: 'SC', name: 'South Carolina', rate: 6.4 },
  { code: 'SD', name: 'South Dakota', rate: 0 },
  { code: 'TN', name: 'Tennessee', rate: 0 },
  { code: 'TX', name: 'Texas', rate: 0 },
  { code: 'UT', name: 'Utah', rate: 4.65 },
  { code: 'VT', name: 'Vermont', rate: 8.75 },
  { code: 'VA', name: 'Virginia', rate: 5.75 },
  { code: 'WA', name: 'Washington', rate: 0 },
  { code: 'WV', name: 'West Virginia', rate: 5.12 },
  { code: 'WI', name: 'Wisconsin', rate: 7.65 },
  { code: 'WY', name: 'Wyoming', rate: 0 },
  { code: 'CUSTOM', name: 'Custom Rate', rate: null },
]

// 2025 Federal Tax Brackets
export const TAX_BRACKETS: Record<string, Array<{ min: number; max: number; rate: number }>> = {
  single: [
    { min: 0, max: 11925, rate: 10 },
    { min: 11925, max: 48475, rate: 12 },
    { min: 48475, max: 103350, rate: 22 },
    { min: 103350, max: 197300, rate: 24 },
    { min: 197300, max: 250525, rate: 32 },
    { min: 250525, max: 626350, rate: 35 },
    { min: 626350, max: Infinity, rate: 37 },
  ],
  mfj: [
    { min: 0, max: 23850, rate: 10 },
    { min: 23850, max: 96950, rate: 12 },
    { min: 96950, max: 206700, rate: 22 },
    { min: 206700, max: 394600, rate: 24 },
    { min: 394600, max: 501050, rate: 32 },
    { min: 501050, max: 751600, rate: 35 },
    { min: 751600, max: Infinity, rate: 37 },
  ],
  hoh: [
    { min: 0, max: 17000, rate: 10 },
    { min: 17000, max: 64850, rate: 12 },
    { min: 64850, max: 103350, rate: 22 },
    { min: 103350, max: 197300, rate: 24 },
    { min: 197300, max: 250500, rate: 32 },
    { min: 250500, max: 626350, rate: 35 },
    { min: 626350, max: Infinity, rate: 37 },
  ],
}

export const STANDARD_DEDUCTIONS: Record<string, number> = {
  single: 15750,
  mfj: 31500,
  hoh: 23625,
}

export const CONTRIBUTION_LIMITS_2025 = {
  traditional401k: 23500,
  traditional401kCatchUp: 31000,
  rothIRA: 7000,
  rothIRACatchUp: 8000,
  hsaIndividual: 4300,
  hsaFamily: 8550,
}

export const ROTH_IRA_PHASEOUT: Record<string, { start: number; end: number }> = {
  single: { start: 150000, end: 165000 },
  mfj: { start: 236000, end: 246000 },
  hoh: { start: 150000, end: 165000 },
}

export function calculateTaxOnIncome(taxableIncome: number, filingStatus = 'single'): number {
  const brackets = TAX_BRACKETS[filingStatus] || TAX_BRACKETS.single
  let tax = 0
  let remaining = Math.max(0, taxableIncome)

  for (const bracket of brackets) {
    const bracketWidth = bracket.max - bracket.min
    const taxableInBracket = Math.min(remaining, bracketWidth)
    if (taxableInBracket <= 0) break
    tax += taxableInBracket * (bracket.rate / 100)
    remaining -= taxableInBracket
  }

  return Math.round(tax * 100) / 100
}

export function calculateMarginalRate(taxableIncome: number, filingStatus = 'single'): number {
  const brackets = TAX_BRACKETS[filingStatus] || TAX_BRACKETS.single
  for (const bracket of brackets) {
    if (taxableIncome <= bracket.max) return bracket.rate
  }
  return 37
}

export function calculateEffectiveTaxRate(taxableIncome: number, filingStatus = 'single'): number {
  if (taxableIncome <= 0) return 0
  const tax = calculateTaxOnIncome(taxableIncome, filingStatus)
  return Math.round((tax / taxableIncome) * 1000) / 10
}

export function getBracketBreakdown(taxableIncome: number, filingStatus = 'single') {
  const brackets = TAX_BRACKETS[filingStatus] || TAX_BRACKETS.single
  const breakdown = []
  let remaining = Math.max(0, taxableIncome)

  for (const bracket of brackets) {
    const bracketWidth = bracket.max === Infinity ? remaining : bracket.max - bracket.min
    const taxableInBracket = Math.min(remaining, bracketWidth)
    if (taxableInBracket <= 0) break
    const taxInBracket = taxableInBracket * (bracket.rate / 100)
    breakdown.push({
      rate: bracket.rate,
      min: bracket.min,
      max: bracket.max,
      taxableAmount: Math.round(taxableInBracket * 100) / 100,
      tax: Math.round(taxInBracket * 100) / 100,
      fillPercent: taxableIncome > 0 ? Math.round((taxableInBracket / taxableIncome) * 1000) / 10 : 0,
    })
    remaining -= taxableInBracket
  }

  return breakdown
}

function generateRothVsTradRecommendation(marginalRate: number, effectiveRate: number, retirementIncome: number, filingStatus: string, grossIncome: number, age: number, annualEmployerMatch = 0, employerMatchRate = 0) {
  const rothAdvantages = [
    'Tax-free withdrawals in retirement',
    'No Required Minimum Distributions (RMDs)',
    'Tax-free inheritance for heirs',
    'Tax diversification in retirement',
    'Contributions can be withdrawn anytime penalty-free',
  ]
  const tradAdvantages = [
    'Immediate tax deduction reduces current tax bill',
    'Lowers AGI (helps with other tax benefits)',
    'More money invested upfront due to tax savings',
    'Higher current cash flow from lower taxes',
  ]

  const matchNote = annualEmployerMatch > 0
    ? `Your employer match adds $${annualEmployerMatch.toLocaleString()}/yr (${employerMatchRate}% of salary) — this is FREE money`
    : null
  const matchReminder = annualEmployerMatch > 0
    ? 'Employer match is always pre-tax, even with Roth 401k — it grows tax-deferred and is taxed at withdrawal'
    : 'Consider whether your employer offers a match — never leave free money on the table'

  let strategy, confidence, nextStep, reasons
  const phaseout = ROTH_IRA_PHASEOUT[filingStatus]
  const isPhasedOut = grossIncome >= phaseout.end
  const isPartialPhaseout = grossIncome >= phaseout.start && grossIncome < phaseout.end

  if (marginalRate <= 12) {
    strategy = 'Strongly favor Roth'
    confidence = 'high'
    nextStep = 'Roth & HSA'
    reasons = [
      `At the ${marginalRate}% bracket, your tax rate is likely lower now than in retirement`,
      'Pay taxes now at historically low rates and let money grow tax-free',
      'Even modest retirement income (Social Security + withdrawals) may push you into a higher bracket',
      'Maximize Roth IRA and HSA first for tax-free growth',
      matchNote,
      matchReminder,
    ].filter(Boolean)
  } else if (marginalRate === 22) {
    strategy = 'Mix: Traditional 401k + Roth IRA'
    confidence = 'medium'
    nextStep = 'Roth & HSA + Max Retirement'
    reasons = [
      `The 22% bracket is the "messy middle" — could go either way`,
      'Use Traditional 401k to capture the deduction and lower AGI',
      'Fund Roth IRA for tax diversification',
      'This gives you both tax-deferred and tax-free buckets in retirement',
      'At 22%, a mix of Traditional + Roth is widely considered optimal',
      matchNote,
      matchReminder,
    ].filter(Boolean)
  } else if (marginalRate === 24) {
    strategy = 'Lean Traditional 401k, still fund Roth IRA'
    confidence = 'medium'
    nextStep = 'Max Retirement + Roth IRA'
    reasons = [
      `At 24%, the Traditional 401k deduction saves meaningful tax dollars now`,
      'Many retirees land in the 12-22% bracket, making Traditional the math winner',
      'Still fund Roth IRA for tax diversification',
      isPartialPhaseout ? 'Note: Your income is in the Roth IRA phase-out range — consider backdoor Roth' : null,
      isPhasedOut ? 'Your income exceeds Roth IRA limits — use backdoor Roth IRA strategy' : null,
      matchNote,
      matchReminder,
    ].filter(Boolean)
  } else {
    strategy = 'Strongly favor Traditional 401k + Backdoor Roth'
    confidence = 'high'
    nextStep = 'Max-Out Retirement'
    reasons = [
      `At ${marginalRate}%, the Traditional 401k deduction is extremely valuable`,
      'High earners almost always benefit from deferring taxes now',
      'Use backdoor Roth IRA for tax-free growth alongside Traditional accounts',
      'The 32%+ bracket rarely persists into retirement for most people',
      'Pre-tax is nearly always correct at high brackets',
      matchNote,
      matchReminder,
    ].filter(Boolean)
  }

  return {
    strategy,
    confidence,
    nextStep,
    reasons,
    rothAdvantages: marginalRate <= 12 ? rothAdvantages : rothAdvantages.slice(0, 3),
    tradAdvantages: marginalRate >= 24 ? tradAdvantages : tradAdvantages.slice(0, 2),
    annualEmployerMatch,
    employerMatchRate,
  }
}

export function compareRothVsTraditional({
  grossIncome,
  filingStatus = 'single',
  currentAge = 30,
  retirementAge = 65,
  annualContribution = 23500,
  expectedReturn = 7,
  expectedRetirementIncome = 0,
  stateTaxRate = 0,
  employerMatch = 0,
  matchAppliesTo = 'both',
  startingRothBalance = 0,
  startingTradBalance = 0,
}: RothVsTraditionalInput) {
  const yearsToRetirement = Math.max(1, retirementAge - currentAge)
  const deduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single
  const taxableIncome = Math.max(0, grossIncome - deduction)

  const currentMarginalRate = calculateMarginalRate(taxableIncome, filingStatus)
  const currentEffectiveRate = calculateEffectiveTaxRate(taxableIncome, filingStatus)
  const currentFederalTax = calculateTaxOnIncome(taxableIncome, filingStatus)

  // Employer match: percentage of gross income, contributed pre-tax by employer
  const annualEmployerMatch = Math.round(grossIncome * (employerMatch / 100))
  const totalAnnualWithMatch = annualContribution + annualEmployerMatch

  // Match allocation per path — employer match is always deposited pre-tax
  // but only applies if the employee contributes to the account the employer matches
  const rothPathGetsMatch = matchAppliesTo === 'both' || matchAppliesTo === 'roth'
  const tradPathGetsMatch = matchAppliesTo === 'both' || matchAppliesTo === 'traditional'

  // Retirement tax rates
  const retirementTaxableIncome = Math.max(0, expectedRetirementIncome - deduction)
  const retirementMarginalRate = calculateMarginalRate(retirementTaxableIncome, filingStatus)
  const retirementEffectiveRate = calculateEffectiveTaxRate(retirementTaxableIncome, filingStatus)

  // Traditional path: employee contributes pre-tax, tax savings invested in brokerage
  const tradTaxSavings = annualContribution * ((currentMarginalRate + stateTaxRate) / 100)

  // Year-by-year projection
  const monthlyReturn = expectedReturn / 100 / 12
  const taxDragFactor = 0.85 // brokerage has capital gains drag
  const brokerageMonthlyReturn = (expectedReturn * taxDragFactor) / 100 / 12
  const monthlyContribution = annualContribution / 12
  const monthlyEmployerMatch = annualEmployerMatch / 12
  const monthlyTaxSavings = tradTaxSavings / 12

  // Initialize with starting balances
  let rothBalance = startingRothBalance
  let rothEmployerBalance = 0 // employer match in Roth path (always pre-tax)
  let tradPreTaxBalance = startingTradBalance
  let tradBrokerageBalance = 0 // reinvested tax savings
  let rothContributions = 0
  let tradContributions = 0

  const yearlyProjection = []

  for (let year = 1; year <= yearsToRetirement; year++) {
    for (let month = 0; month < 12; month++) {
      // Roth path: employee after-tax contributions grow tax-free
      rothBalance = rothBalance * (1 + monthlyReturn) + monthlyContribution
      // Employer match goes pre-tax (if this path gets the match)
      if (rothPathGetsMatch) {
        rothEmployerBalance = rothEmployerBalance * (1 + monthlyReturn) + monthlyEmployerMatch
      } else {
        rothEmployerBalance = rothEmployerBalance * (1 + monthlyReturn)
      }
      rothContributions += monthlyContribution

      // Traditional path: employee + employer both pre-tax, grow tax-deferred
      tradPreTaxBalance = tradPreTaxBalance * (1 + monthlyReturn) + monthlyContribution +
        (tradPathGetsMatch ? monthlyEmployerMatch : 0)
      tradContributions += monthlyContribution

      // Tax savings invested in brokerage (with cap gains drag)
      tradBrokerageBalance = tradBrokerageBalance * (1 + brokerageMonthlyReturn) + monthlyTaxSavings
    }

    // Roth after-tax: Roth balance is tax-free + employer match taxed at retirement rate
    const rothAfterTax = rothBalance +
      rothEmployerBalance * (1 - (retirementEffectiveRate + stateTaxRate) / 100)

    const tradAfterTax = tradPreTaxBalance * (1 - (retirementEffectiveRate + stateTaxRate) / 100) +
      tradBrokerageBalance * (1 - (Math.min(15, stateTaxRate) / 100)) // LTCG rate on brokerage

    yearlyProjection.push({
      year,
      age: currentAge + year,
      rothBalance: Math.round(rothBalance),
      rothEmployerBalance: Math.round(rothEmployerBalance),
      rothContributions: Math.round(rothContributions),
      tradPreTaxBalance: Math.round(tradPreTaxBalance),
      tradBrokerageBalance: Math.round(tradBrokerageBalance),
      tradTotalBalance: Math.round(tradPreTaxBalance + tradBrokerageBalance),
      tradAfterTax: Math.round(tradAfterTax),
      rothAfterTax: Math.round(rothAfterTax),
      advantage: Math.round(rothAfterTax - tradAfterTax),
    })
  }

  const finalRothAfterTax = Math.round(
    rothBalance + rothEmployerBalance * (1 - (retirementEffectiveRate + stateTaxRate) / 100)
  )
  const finalTradAfterTax = Math.round(
    tradPreTaxBalance * (1 - (retirementEffectiveRate + stateTaxRate) / 100) +
    tradBrokerageBalance * (1 - (Math.min(15, stateTaxRate) / 100))
  )
  const winner = finalRothAfterTax >= finalTradAfterTax ? 'roth' : 'traditional'
  const advantageAmount = Math.abs(finalRothAfterTax - finalTradAfterTax)

  // Break-even: find the retirement effective rate where Roth = Traditional
  let breakEvenRate = null
  for (let testRate = 0; testRate <= 40; testRate += 0.5) {
    const testRothAfterTax = rothBalance +
      rothEmployerBalance * (1 - (testRate + stateTaxRate) / 100)
    const testTradAfterTax = tradPreTaxBalance * (1 - (testRate + stateTaxRate) / 100) +
      tradBrokerageBalance * (1 - (Math.min(15, stateTaxRate) / 100))
    if (testRothAfterTax >= testTradAfterTax) {
      breakEvenRate = testRate
      break
    }
  }

  // Break-even analysis data for chart
  const breakEvenData = []
  for (let testRate = 5; testRate <= 35; testRate += 5) {
    const testRothAfterTax = rothBalance +
      rothEmployerBalance * (1 - (testRate + stateTaxRate) / 100)
    const testTradAfterTax = tradPreTaxBalance * (1 - (testRate + stateTaxRate) / 100) +
      tradBrokerageBalance * (1 - (Math.min(15, stateTaxRate) / 100))
    breakEvenData.push({
      retirementRate: testRate,
      label: `${testRate}%`,
      rothAfterTax: Math.round(testRothAfterTax),
      tradAfterTax: Math.round(testTradAfterTax),
      rothWins: testRothAfterTax >= testTradAfterTax,
    })
  }

  const beyondMatch = generateBeyondMatchStrategy(
    currentMarginalRate, filingStatus, grossIncome, employerMatch, annualEmployerMatch, annualContribution
  )

  const recommendation = generateRothVsTradRecommendation(
    currentMarginalRate, currentEffectiveRate, expectedRetirementIncome,
    filingStatus, grossIncome, currentAge, annualEmployerMatch, employerMatch
  )

  const bracketBreakdown = getBracketBreakdown(taxableIncome, filingStatus)

  return {
    currentMarginalRate,
    currentEffectiveRate,
    currentFederalTax,
    taxableIncome,
    retirementMarginalRate,
    retirementEffectiveRate,
    annualTaxSavings: Math.round(tradTaxSavings),
    annualEmployerMatch,
    employerMatchRate: employerMatch,
    totalAnnualWithMatch,
    matchAppliesTo,
    finalRothAfterTax,
    finalTradAfterTax,
    winner,
    advantageAmount,
    breakEvenRate,
    breakEvenData,
    yearlyProjection,
    recommendation,
    beyondMatch,
    bracketBreakdown,
    rothIRAEligibility: getRothIRAEligibility(grossIncome, filingStatus),
    startingRothBalance,
    startingTradBalance,
  }
}

function generateBeyondMatchStrategy(marginalRate: number, filingStatus: string, grossIncome: number, matchRate: number, annualMatch: number, annualContribution: number) {
  const matchContribution = Math.round(grossIncome * (matchRate / 100))
  const beyondMatchAmount = Math.max(0, annualContribution - matchContribution)
  const rothEligibility = getRothIRAEligibility(grossIncome, filingStatus)
  const rothIRALimit = rothEligibility.eligible ? rothEligibility.maxContribution : 0
  const remaining401kSpace = Math.max(0, CONTRIBUTION_LIMITS_2025.traditional401k - annualContribution)

  const steps = []

  // Step 1 is always: get the match
  if (matchRate > 0) {
    steps.push({
      order: 1,
      action: `Contribute ${matchRate}% to 401(k) to capture full employer match`,
      amount: matchContribution,
      account: '401(k)',
      nextStep: 'Employer Match',
      rationale: `Employer match is an instant ${matchRate > 0 ? '50-100' : '0'}% return — the best guaranteed return in finance`,
    })
  }

  // Steps 2+ depend on bracket
  if (marginalRate <= 12) {
    // Low bracket: Roth everything
    if (rothIRALimit > 0) {
      steps.push({
        order: 2,
        action: `Max Roth IRA ($${rothIRALimit.toLocaleString()}/yr)`,
        amount: rothIRALimit,
        account: 'Roth IRA',
        nextStep: 'Roth & HSA',
        rationale: `At ${marginalRate}%, pay taxes now at historically low rates. Roth IRA gives tax-free growth + no RMDs`,
      })
    }
    steps.push({
      order: rothIRALimit > 0 ? 3 : 2,
      action: 'Contribute remaining to Roth 401(k)',
      amount: beyondMatchAmount > 0 ? beyondMatchAmount : null,
      account: 'Roth 401(k)',
      nextStep: 'Max Retirement',
      rationale: `At ${marginalRate}%, Roth contributions are strongly favored — your rate is almost certainly lower now than in retirement`,
    })
  } else if (marginalRate === 22) {
    // Messy middle: Roth IRA + Traditional 401k
    if (rothIRALimit > 0) {
      steps.push({
        order: 2,
        action: `Max Roth IRA ($${rothIRALimit.toLocaleString()}/yr)`,
        amount: rothIRALimit,
        account: 'Roth IRA',
        nextStep: 'Roth & HSA',
        rationale: 'At 22% (the "messy middle"), Roth IRA adds tax diversification — you want both pre-tax and tax-free buckets',
      })
    }
    steps.push({
      order: rothIRALimit > 0 ? 3 : 2,
      action: 'Max out Traditional 401(k) with remaining space',
      amount: remaining401kSpace > 0 ? remaining401kSpace : null,
      account: 'Traditional 401(k)',
      nextStep: 'Max Retirement',
      rationale: 'Traditional 401(k) deduction at 22% is solid — many retirees end up in the 12% bracket, making this a winning bet',
    })
  } else if (marginalRate === 24) {
    // Lean Traditional
    steps.push({
      order: 2,
      action: 'Max out Traditional 401(k)',
      amount: remaining401kSpace > 0 ? remaining401kSpace : null,
      account: 'Traditional 401(k)',
      nextStep: 'Max Retirement',
      rationale: `At 24%, the deduction saves $${Math.round(remaining401kSpace * 0.24).toLocaleString()} in federal tax. Most retirees land in 12-22%`,
    })
    if (rothIRALimit > 0) {
      steps.push({
        order: 3,
        action: `Fund Roth IRA ($${rothIRALimit.toLocaleString()}/yr)`,
        amount: rothIRALimit,
        account: 'Roth IRA',
        nextStep: 'Roth & HSA',
        rationale: 'Still fund Roth IRA for tax diversification — having tax-free money in retirement provides flexibility',
      })
    } else if (rothEligibility.status === 'phased-out') {
      steps.push({
        order: 3,
        action: 'Backdoor Roth IRA ($7,000/yr)',
        amount: CONTRIBUTION_LIMITS_2025.rothIRA,
        account: 'Backdoor Roth IRA',
        nextStep: 'Roth & HSA',
        rationale: 'Income exceeds Roth IRA limits — backdoor Roth lets you still get tax-free growth for diversification',
      })
    }
  } else {
    // High bracket (32%+): Traditional first, backdoor Roth
    steps.push({
      order: 2,
      action: 'Max out Traditional 401(k)',
      amount: remaining401kSpace > 0 ? remaining401kSpace : null,
      account: 'Traditional 401(k)',
      nextStep: 'Max Retirement',
      rationale: `At ${marginalRate}%, the deduction is extremely valuable. Pre-tax is nearly always correct at high brackets`,
    })
    steps.push({
      order: 3,
      action: 'Backdoor Roth IRA ($7,000/yr)',
      amount: CONTRIBUTION_LIMITS_2025.rothIRA,
      account: 'Backdoor Roth IRA',
      nextStep: 'Roth & HSA',
      rationale: 'Use backdoor Roth for tax diversification — no income limits, gives you tax-free money alongside Traditional',
    })
  }

  return {
    steps,
    beyondMatchAmount,
    summary: marginalRate <= 12
      ? 'After the match, go all-in on Roth — your low tax rate makes paying taxes now the clear winner'
      : marginalRate === 22
        ? 'After the match, mix it up — Roth IRA for tax diversification, then Traditional 401(k) for the deduction'
        : `After the match, maximize Traditional 401(k) for the ${marginalRate}% deduction, then add Roth for diversification`,
  }
}

// ========== DEBT VS INVEST COMPARISON ==========

interface DebtVsInvestInput {
  loanBalance: number
  loanRate: number       // annual %
  minimumPayment: number
  extraMonthly: number
  investReturnRate: number // annual %
}

interface DebtVsInvestMonth {
  month: number
  // Path A: pay extra toward loan
  a_loanBalance: number
  a_investBalance: number
  a_netWorth: number
  a_totalPaid: number
  // Path B: invest the extra
  b_loanBalance: number
  b_investBalance: number
  b_netWorth: number
  b_totalPaid: number
}

export function calculateDebtVsInvest({ loanBalance, loanRate, minimumPayment, extraMonthly, investReturnRate }: DebtVsInvestInput) {
  const loanMonthlyRate = loanRate / 100 / 12
  const investMonthlyRate = investReturnRate / 100 / 12
  const MAX = 600

  // ===== Baseline: full loan term with minimum payments only =====
  let baseBalance = loanBalance
  let baseInterest = 0
  let basePayoffMonth = 0
  for (let m = 1; m <= MAX && baseBalance > 0.01; m++) {
    const interest = baseBalance * loanMonthlyRate
    baseInterest += interest
    const payment = Math.min(minimumPayment, baseBalance + interest)
    baseBalance = Math.max(0, baseBalance + interest - payment)
    if (baseBalance <= 0.01) basePayoffMonth = m
  }
  if (basePayoffMonth === 0) basePayoffMonth = MAX
  const baselineTotalInterest = Math.round(baseInterest * 100) / 100
  const baselinePayoffMonth = basePayoffMonth

  // ===== Run both paths for the FULL loan term =====
  // Path A: extra goes to loan, then after payoff invest (min + extra)
  // Path B: minimum on loan, extra invested from day one
  const fullTermMonths = baselinePayoffMonth

  let a_loan = loanBalance
  let a_invest = 0
  let a_totalPaid = 0
  let a_totalInterest = 0
  let a_payoffMonth = 0
  let a_loanPaidOff = false
  let a_totalContributed = 0
  let a_totalInvestGrowth = 0

  let b_loan = loanBalance
  let b_invest = 0
  let b_totalPaid = 0
  let b_totalInterest = 0
  let b_totalContributed = 0
  let b_totalInvestGrowth = 0

  const schedule: DebtVsInvestMonth[] = []

  for (let month = 1; month <= fullTermMonths; month++) {
    // ===== Path A: Extra goes to loan, then invest after payoff =====
    if (!a_loanPaidOff && a_loan > 0.01) {
      const a_interest = a_loan * loanMonthlyRate
      a_totalInterest += a_interest
      const a_payment = Math.min(minimumPayment + extraMonthly, a_loan + a_interest)
      a_loan = Math.max(0, a_loan + a_interest - a_payment)
      a_totalPaid += a_payment
      if (a_loan <= 0.01) {
        a_loanPaidOff = true
        a_loan = 0
        a_payoffMonth = month
      }
    }
    // After loan is paid off, invest the full amount (min payment + extra)
    if (a_loanPaidOff) {
      const growthA = a_invest * investMonthlyRate
      a_totalInvestGrowth += growthA
      a_invest += growthA + minimumPayment + extraMonthly
      a_totalContributed += minimumPayment + extraMonthly
    }

    // ===== Path B: Minimum on loan, extra invested =====
    if (b_loan > 0.01) {
      const b_interest = b_loan * loanMonthlyRate
      b_totalInterest += b_interest
      const b_payment = Math.min(minimumPayment, b_loan + b_interest)
      b_loan = Math.max(0, b_loan + b_interest - b_payment)
      b_totalPaid += b_payment
    }
    const growthB = b_invest * investMonthlyRate
    b_totalInvestGrowth += growthB
    b_invest += growthB + extraMonthly
    b_totalContributed += extraMonthly

    schedule.push({
      month,
      a_loanBalance: Math.round(a_loan * 100) / 100,
      a_investBalance: Math.round(a_invest * 100) / 100,
      a_netWorth: Math.round((a_invest - a_loan) * 100) / 100,
      a_totalPaid: Math.round(a_totalPaid * 100) / 100,
      b_loanBalance: Math.round(b_loan * 100) / 100,
      b_investBalance: Math.round(b_invest * 100) / 100,
      b_netWorth: Math.round((b_invest - b_loan) * 100) / 100,
      b_totalPaid: Math.round(b_totalPaid * 100) / 100,
    })
  }

  const interestSaved = Math.round((baselineTotalInterest - a_totalInterest) * 100) / 100
  const timeSaved = baselinePayoffMonth - (a_payoffMonth || fullTermMonths)
  const extraInterestCost = Math.round((b_totalInterest - a_totalInterest) * 100) / 100

  const final = schedule[schedule.length - 1]
  const a_netWorth = final ? final.a_netWorth : 0
  const b_netWorth = final ? final.b_netWorth : 0
  const b_remainingLoan = final ? final.b_loanBalance : 0

  // Path B adjusted: investment gains minus extra interest cost of not paying off early
  const b_adjustedNetWorth = Math.round((b_netWorth - extraInterestCost) * 100) / 100

  // Compare Path A net worth vs Path B adjusted net worth
  const winner = a_netWorth >= b_adjustedNetWorth ? 'payoff' : 'invest'
  const advantage = Math.round(Math.abs(a_netWorth - b_adjustedNetWorth) * 100) / 100

  return {
    schedule,
    fullTermMonths,
    a_payoffMonth: a_payoffMonth || fullTermMonths,
    a_totalInterest: Math.round(a_totalInterest * 100) / 100,
    a_finalInvestment: final ? Math.round(final.a_investBalance * 100) / 100 : 0,
    a_totalContributed: Math.round(a_totalContributed * 100) / 100,
    a_totalInvestGrowth: Math.round(a_totalInvestGrowth * 100) / 100,
    a_netWorth: Math.round(a_netWorth * 100) / 100,
    b_totalInterest: Math.round(b_totalInterest * 100) / 100,
    b_remainingLoan: Math.round(b_remainingLoan * 100) / 100,
    extraInterestCost,
    b_adjustedNetWorth,
    // Baseline comparison
    baselinePayoffMonth,
    baselineTotalInterest,
    interestSaved,
    timeSaved,
    // Investment details
    b_finalInvestment: final ? Math.round(final.b_investBalance * 100) / 100 : 0,
    b_totalContributed: Math.round(b_totalContributed * 100) / 100,
    b_totalInvestGrowth: Math.round(b_totalInvestGrowth * 100) / 100,
    b_netWorth: Math.round(b_netWorth * 100) / 100,
    // Winner
    winner,
    advantage,
  }
}

// ========== PAYCHECK CALCULATOR ==========

export const FICA_CONSTANTS = {
  ssWageBase: 176100,
  ssRate: 0.062,
  medicareRate: 0.0145,
  additionalMedicareRate: 0.009,
  additionalMedicareThreshold: 200000,
}

export const PAY_FREQUENCIES: Record<string, { label: string; periods: number }> = {
  weekly: { label: 'Weekly', periods: 52 },
  biweekly: { label: 'Bi-Weekly', periods: 26 },
  semimonthly: { label: 'Semi-Monthly', periods: 24 },
  monthly: { label: 'Monthly', periods: 12 },
}

interface PaycheckInput {
  grossPay: number
  payFrequency: string
  filingStatus: string
  stateCode: string
  customStateTaxRate?: number
  // Pre-tax deductions
  traditional401kPercent: number
  traditional401kDollar: number
  use401kPercent: boolean
  hsaPerPeriod: number
  healthInsurance: number
  // Post-tax deductions
  roth401kPercent: number
  roth401kDollar: number
  useRoth401kPercent: boolean
  otherPostTax: number
  loanRepayment401k: number
  unionDues: number
  lifeDisabilityInsurance: number
  otherMiscPostTax: number
  // W-4 adjustments
  extraWithholding: number
  dependentsCredit: number
  otherIncome: number
  deductionsAboveStandard: number
  // Pay stub actuals (optional)
  actualFederalWithheld?: number | null
  actualStateWithheld?: number | null
  actualSSWithheld?: number | null
  actualMedicareWithheld?: number | null
}

export interface PaycheckResult {
  grossPay: number
  // Pre-tax deductions
  traditional401k: number
  hsa: number
  healthInsurance: number
  totalPreTax: number
  // Taxes
  federalTax: number
  stateTax: number
  socialSecurity: number
  medicare: number
  totalTax: number
  // Post-tax deductions
  roth401k: number
  otherPostTax: number
  loanRepayment401k: number
  unionDues: number
  lifeDisabilityInsurance: number
  otherMiscPostTax: number
  totalPostTax: number
  // Net
  netPay: number
  effectiveRate: number
  // Annual equivalents
  annualGross: number
  annualFederalTax: number
  annualStateTax: number
  annualSS: number
  annualMedicare: number
  annualNet: number
  // Pay stub actuals (null when not provided)
  actualFederalWithheld: number | null
  actualStateWithheld: number | null
  actualSSWithheld: number | null
  actualMedicareWithheld: number | null
}

export function calculatePaycheck(input: PaycheckInput): PaycheckResult {
  const freq = PAY_FREQUENCIES[input.payFrequency] || PAY_FREQUENCIES.biweekly
  const periods = freq.periods

  const grossPay = input.grossPay
  const annualGross = grossPay * periods

  // Pre-tax deductions per period
  const traditional401k = input.use401kPercent
    ? Math.round(grossPay * (input.traditional401kPercent / 100) * 100) / 100
    : input.traditional401kDollar
  const hsa = input.hsaPerPeriod
  const healthInsurance = input.healthInsurance
  const totalPreTax = traditional401k + hsa + healthInsurance

  // Post-tax deductions per period
  const roth401k = input.useRoth401kPercent
    ? Math.round(grossPay * (input.roth401kPercent / 100) * 100) / 100
    : input.roth401kDollar
  const otherPostTax = input.otherPostTax
  const loanRepayment401k = input.loanRepayment401k || 0
  const unionDues = input.unionDues || 0
  const lifeDisabilityInsurance = input.lifeDisabilityInsurance || 0
  const otherMiscPostTax = input.otherMiscPostTax || 0
  const totalPostTax = roth401k + otherPostTax + loanRepayment401k + unionDues + lifeDisabilityInsurance + otherMiscPostTax

  // Annualize for federal tax calculation
  const annualPreTaxDeductions = totalPreTax * periods
  const filingStatus = input.filingStatus || 'single'
  const deduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single

  // W-4 adjustments
  const adjustedAnnualIncome = annualGross - annualPreTaxDeductions + (input.otherIncome || 0)
  const adjustedDeduction = deduction + (input.deductionsAboveStandard || 0)
  const annualDependentsCredit = (input.dependentsCredit || 0)

  const taxableIncome = Math.max(0, adjustedAnnualIncome - adjustedDeduction)
  const annualFederalTax = Math.max(0, calculateTaxOnIncome(taxableIncome, filingStatus) - annualDependentsCredit)
  let federalTax = Math.round((annualFederalTax / periods) * 100) / 100
  federalTax += (input.extraWithholding || 0)

  // State tax (flat rate from STATE_TAX_DATA)
  const stateEntry = STATE_TAX_DATA.find(s => s.code === input.stateCode)
  const stateRate = stateEntry?.code === 'CUSTOM'
    ? (input.customStateTaxRate || 0)
    : (stateEntry?.rate || 0)
  const annualStateTaxableIncome = Math.max(0, annualGross - annualPreTaxDeductions - adjustedDeduction)
  const annualStateTax = annualStateTaxableIncome * (stateRate / 100)
  const stateTax = Math.round((annualStateTax / periods) * 100) / 100

  // FICA on gross pay (pre-tax deductions don't reduce FICA)
  const annualGrossForFICA = annualGross
  const ssWages = Math.min(annualGrossForFICA, FICA_CONSTANTS.ssWageBase)
  const annualSS = ssWages * FICA_CONSTANTS.ssRate
  const socialSecurity = Math.round((annualSS / periods) * 100) / 100

  let annualMedicare = annualGrossForFICA * FICA_CONSTANTS.medicareRate
  if (annualGrossForFICA > FICA_CONSTANTS.additionalMedicareThreshold) {
    annualMedicare += (annualGrossForFICA - FICA_CONSTANTS.additionalMedicareThreshold) * FICA_CONSTANTS.additionalMedicareRate
  }
  const medicare = Math.round((annualMedicare / periods) * 100) / 100

  const totalTax = federalTax + stateTax + socialSecurity + medicare
  const netPay = Math.round((grossPay - totalPreTax - totalTax - totalPostTax) * 100) / 100

  const effectiveRate = grossPay > 0
    ? Math.round((totalTax / grossPay) * 1000) / 10
    : 0

  return {
    grossPay,
    traditional401k,
    hsa,
    healthInsurance,
    totalPreTax,
    federalTax,
    stateTax,
    socialSecurity,
    medicare,
    totalTax,
    roth401k,
    otherPostTax,
    loanRepayment401k,
    unionDues,
    lifeDisabilityInsurance,
    otherMiscPostTax,
    totalPostTax,
    netPay,
    effectiveRate,
    annualGross,
    annualFederalTax: Math.round(annualFederalTax * 100) / 100,
    annualStateTax: Math.round(annualStateTax * 100) / 100,
    annualSS: Math.round(annualSS * 100) / 100,
    annualMedicare: Math.round(annualMedicare * 100) / 100,
    annualNet: Math.round(netPay * periods * 100) / 100,
    actualFederalWithheld: input.actualFederalWithheld ?? null,
    actualStateWithheld: input.actualStateWithheld ?? null,
    actualSSWithheld: input.actualSSWithheld ?? null,
    actualMedicareWithheld: input.actualMedicareWithheld ?? null,
  }
}

function getRothIRAEligibility(grossIncome: number, filingStatus: string) {
  const phaseout = ROTH_IRA_PHASEOUT[filingStatus]
  if (grossIncome < phaseout.start) return { eligible: true, status: 'full', maxContribution: CONTRIBUTION_LIMITS_2025.rothIRA }
  if (grossIncome >= phaseout.end) return { eligible: false, status: 'phased-out', maxContribution: 0, note: 'Consider backdoor Roth IRA' }
  const fraction = 1 - (grossIncome - phaseout.start) / (phaseout.end - phaseout.start)
  return {
    eligible: true,
    status: 'partial',
    maxContribution: Math.round(CONTRIBUTION_LIMITS_2025.rothIRA * fraction / 10) * 10,
    note: 'Partial contribution allowed; consider backdoor Roth for full amount',
  }
}
